'use client';
import { useRef, useEffect, useCallback, useMemo, useState, memo } from "react";
import { gsap } from "gsap";
import { InertiaPlugin } from "gsap/InertiaPlugin";

gsap.registerPlugin(InertiaPlugin);

const throttle = (func, limit) => {
  let lastCall = 0;
  return function (...args) {
    const now = performance.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      func.apply(this, args);
    }
  };
};

function hexToRgb(hex) {
  const m = hex.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!m) return { r: 0, g: 0, b: 0 };
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  };
}

// Letter patterns for dot grid (5x7 grid per letter)
const letterPatterns = {
  'A': [
    [0,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,1,1,1,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1]
  ],
  'B': [
    [1,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,1,1,1,0]
  ],
  'C': [
    [0,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,0,0,0,1],
    [0,1,1,1,0]
  ],
  'D': [
    [1,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,1,1,1,0]
  ],
  'E': [
    [1,1,1,1,1],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,1,1,1,0],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,1,1,1,1]
  ],
  'F': [
    [1,1,1,1,1],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,1,1,1,0],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,0,0,0,0]
  ],
  'G': [
    [0,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,0],
    [1,0,1,1,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [0,1,1,1,0]
  ],
  'H': [
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,1,1,1,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1]
  ],
  'I': [
    [0,1,1,1,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,1,1,1,0]
  ],
  'J': [
    [0,0,0,0,1],
    [0,0,0,0,1],
    [0,0,0,0,1],
    [0,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [0,1,1,1,0]
  ],
  'K': [
    [1,0,0,0,1],
    [1,0,0,1,0],
    [1,0,1,0,0],
    [1,1,0,0,0],
    [1,0,1,0,0],
    [1,0,0,1,0],
    [1,0,0,0,1]
  ],
  'L': [
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,1,1,1,1]
  ],
  'M': [
    [1,0,0,0,1],
    [1,1,0,1,1],
    [1,0,1,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1]
  ],
  'N': [
    [1,0,0,0,1],
    [1,1,0,0,1],
    [1,0,1,0,1],
    [1,0,0,1,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1]
  ],
  'O': [
    [0,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [0,1,1,1,0]
  ],
  'P': [
    [1,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,1,1,1,0],
    [1,0,0,0,0],
    [1,0,0,0,0],
    [1,0,0,0,0]
  ],
  'Q': [
    [0,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,1,0,1],
    [1,0,0,1,0],
    [0,1,1,0,1]
  ],
  'R': [
    [1,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,1,1,1,0],
    [1,0,1,0,0],
    [1,0,0,1,0],
    [1,0,0,0,1]
  ],
  'S': [
    [0,1,1,1,0],
    [1,0,0,0,1],
    [1,0,0,0,0],
    [0,1,1,1,0],
    [0,0,0,0,1],
    [1,0,0,0,1],
    [0,1,1,1,0]
  ],
  'T': [
    [1,1,1,1,1],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0]
  ],
  'U': [
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [0,1,1,1,0]
  ],
  'V': [
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [0,1,0,1,0],
    [0,0,1,0,0]
  ],
  'W': [
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,0,0,1],
    [1,0,1,0,1],
    [1,1,0,1,1],
    [1,0,0,0,1]
  ],
  'X': [
    [1,0,0,0,1],
    [0,1,0,1,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,1,0,1,0],
    [1,0,0,0,1]
  ],
  'Y': [
    [1,0,0,0,1],
    [0,1,0,1,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0],
    [0,0,1,0,0]
  ],
  'Z': [
    [1,1,1,1,1],
    [0,0,0,0,1],
    [0,0,0,1,0],
    [0,0,1,0,0],
    [0,1,0,0,0],
    [1,0,0,0,0],
    [1,1,1,1,1]
  ],
  ' ': [
    [0,0,0,0,0],
    [0,0,0,0,0],
    [0,0,0,0,0],
    [0,0,0,0,0],
    [0,0,0,0,0],
    [0,0,0,0,0],
    [0,0,0,0,0]
  ]
};

const DotGrid = ({
  dotSize = 20,
  gap = 40,
  baseColor = "#5227FF",
  activeColor = "#5227FF",
  proximity = 150,
  speedTrigger = 100,
  shockRadius = 250,
  shockStrength = 5,
  maxSpeed = 5000,
  resistance = 750,
  returnDuration = 1.5,
  autonomousDelay = 3000, // 3 seconds of inactivity before autonomous mode
  autonomousSpeed = 0.5, // Speed of autonomous cursor movement
  className = "",
  style,
  enableNameFeature = false, // New prop to enable the name feature
  userName = '', // External userName prop
  demoText = '', // Demo text to cycle through
  nameSubmitted = false, // External nameSubmitted prop
  nameDotsRef = null, // External nameDotsRef prop
  nameAnimationRef = null, // External nameAnimationRef prop
  effectMode = 'normal', // Effect mode prop
}) => {
  const wrapperRef = useRef(null);
  const canvasRef = useRef(null);
  const dotsRef = useRef([]);
  const pointerRef = useRef({
    x: 0,
    y: 0,
    vx: 0,
    vy: 0,
    speed: 0,
    lastTime: 0,
    lastX: 0,
    lastY: 0,
  });

  // Use external refs or create internal fallbacks
  const internalNameDotsRef = useRef([]);
  const internalNameAnimationRef = useRef(null);
  const actualNameDotsRef = nameDotsRef || internalNameDotsRef;
  const actualNameAnimationRef = nameAnimationRef || internalNameAnimationRef;

  const clickTrackerRef = useRef([]);

  // Autonomous mode state
  const autonomousRef = useRef({
    active: false,
    timeoutId: null,
    animationId: null,
    targetX: 0,
    targetY: 0,
    currentX: 0,
    currentY: 0,
    lastTargetChange: 0,
    targetChangeInterval: 2000,
    phase: 'burst', // 'burst', 'glide', 'explode', 'reset'
    phaseStartTime: 0,
    burstDuration: 2000, // 2 seconds for burst phase
    glideDuration: 4000, // 4 seconds for glide phase
    explodeDuration: 1000, // 1 second for explode phase
    resetDuration: 1000, // 1 second for reset phase
    burstIntensity: 0,
    explodeRadius: 0,
    explodeCenter: {x: 0, y: 0},
    cycleStartTime: 0,
    rainbowWash: false,
    lastRainbowTime: 0,
    rainbowInterval: 50000,
    rainbowDuration: 10000,
    clickTriggered: false,
    clickExplodeFrom: null,
  });

  const baseRgb = useMemo(() => hexToRgb(baseColor), [baseColor]);
  const activeRgb = useMemo(() => hexToRgb(activeColor), [activeColor]);

  const circlePath = useMemo(() => {
    if (typeof window === "undefined" || !window.Path2D) return null;

    const p = new Path2D();
    p.arc(0, 0, dotSize / 2, 0, Math.PI * 2);
    return p;
  }, [dotSize]);

  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  const buildGrid = useCallback(() => {
    const wrap = wrapperRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const { width, height } = wrap.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.scale(dpr, dpr);

    const cols = Math.floor((width + gap) / (dotSize + gap));
    const rows = Math.floor((height + gap) / (dotSize + gap));
    const cell = dotSize + gap;

    const gridW = cell * cols - gap;
    const gridH = cell * rows - gap;

    const extraX = width - gridW;
    const extraY = height - gridH;

    const startX = extraX / 2 + dotSize / 2;
    const startY = extraY / 2 + dotSize / 2;

    const dots = [];
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        const cx = startX + x * cell;
        const cy = startY + y * cell;
        dots.push({ cx, cy, xOffset: 0, yOffset: 0, _inertiaApplied: false });
      }
    }
    dotsRef.current = dots;

    // Update autonomous bounds
    autonomousRef.current.bounds = { width, height };
  }, [dotSize, gap]);

  // Name processing functions
  const processName = useCallback((name) => {
    if (!name.trim()) return [];
    
    const canvas = canvasRef.current;
    if (!canvas) return [];
    
    const rect = canvas.getBoundingClientRect();
    const nameUpper = name.toUpperCase().slice(0, 25); // Limit to 25 characters
    const nameDots = [];
    
    // Calculate positioning - place name in lower section below hero content
    const letterWidth = 5;
    const letterHeight = 7;
    const letterSpacing = 1;
    const totalWidth = nameUpper.length * letterWidth + (nameUpper.length - 1) * letterSpacing;
    const gridCols = Math.floor(rect.width / (dotSize + gap));
    const gridRows = Math.floor(rect.height / (dotSize + gap));
    
    // Position horizontally centered
    const startX = Math.floor((gridCols - totalWidth) / 2);
    
    // Position in lower 25% of screen (below hero content)
    const startY = Math.floor(gridRows * 0.75) - Math.floor(letterHeight / 2);
    
    // Convert name to dot positions
    for (let letterIndex = 0; letterIndex < nameUpper.length; letterIndex++) {
      const letter = nameUpper[letterIndex];
      const pattern = letterPatterns[letter] || letterPatterns[' '];
      
      for (let y = 0; y < letterHeight; y++) {
        for (let x = 0; x < letterWidth; x++) {
          if (pattern[y][x]) {
            const dotX = startX + letterIndex * (letterWidth + letterSpacing) + x;
            const dotY = startY + y;
            
            // Find corresponding dot in grid
            const dotIndex = dotY * Math.floor((rect.width + gap) / (dotSize + gap)) + dotX;
            if (dotIndex >= 0 && dotIndex < dotsRef.current.length) {
              nameDots.push({
                index: dotIndex,
                dot: dotsRef.current[dotIndex],
                letterIndex,
                x: dotX,
                y: dotY,
                delay: letterIndex * 200 + Math.random() * 100
              });
            }
          }
        }
      }
    }
    
    return nameDots;
  }, [dotSize, gap]);

  const animateNameEntry = useCallback((nameDots) => {
    if (!nameDots.length) return;
    
    // Clear any existing name animation
    if (actualNameAnimationRef.current) {
      actualNameAnimationRef.current.kill();
    }
    
    const tl = gsap.timeline();
    
    // Animate each letter appearing
    nameDots.forEach((nameDot, index) => {
      const { dot, delay } = nameDot;
      
      tl.to(dot, {
        xOffset: 0,
        yOffset: -20,
        duration: 0.8,
        ease: "elastic.out(1, 0.75)",
        delay: delay / 1000
      }, 0)
      .to(dot, {
        yOffset: 0,
        duration: 0.6,
        ease: "bounce.out",
        delay: (delay + 400) / 1000
      }, 0);
    });
    
    // Add special effects
    tl.call(() => {
      // Rainbow wave effect
      let waveIndex = 0;
      const waveInterval = setInterval(() => {
        if (waveIndex >= nameDots.length) {
          clearInterval(waveInterval);
          return;
        }
        
        const nameDot = nameDots[waveIndex];
        const { dot } = nameDot;
        
        gsap.to(dot, {
          xOffset: dot.xOffset + (Math.random() - 0.5) * 10,
          yOffset: dot.yOffset + (Math.random() - 0.5) * 10,
          duration: 0.3,
          ease: "power2.out",
          yoyo: true,
          repeat: 1
        });
        
        waveIndex++;
      }, 50);
    }, null, 1);
    
    actualNameAnimationRef.current = tl;
  }, [actualNameAnimationRef]);

  // Process name when nameSubmitted changes or demo text cycles
  useEffect(() => {
    if (enableNameFeature) {
      if (nameSubmitted && userName.trim()) {
        // User has submitted their name
        const nameDots = processName(userName);
        actualNameDotsRef.current = nameDots;
        
        if (nameDots.length > 0) {
          animateNameEntry(nameDots);
        }
      } else if (!nameSubmitted && demoText.trim()) {
        // Show cycling demo text
        const nameDots = processName(demoText);
        actualNameDotsRef.current = nameDots;
        
        if (nameDots.length > 0) {
          animateNameEntry(nameDots);
        }
      }
    }
  }, [nameSubmitted, userName, demoText, enableNameFeature, processName, animateNameEntry, actualNameDotsRef]);


  const stopAutonomousMode = useCallback(() => {
    const autonomous = autonomousRef.current;
    if (!autonomous.active) return;

    autonomous.active = false;
    if (autonomous.animationId) {
      cancelAnimationFrame(autonomous.animationId);
      autonomous.animationId = null;
    }
    if (autonomous.timeoutId) {
      clearTimeout(autonomous.timeoutId);
      autonomous.timeoutId = null;
    }
  }, []);

  const startAutonomousMode = useCallback(() => {
    const autonomous = autonomousRef.current;
    if (autonomous.active) return;

    autonomous.active = true;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();

    // Start from current pointer position or center
    autonomous.currentX = pointerRef.current.x || rect.width / 2;
    autonomous.currentY = pointerRef.current.y || rect.height / 2;

    // Initialize cycle
    if (autonomous.clickTriggered) {
      autonomous.phase = 'explode';
      autonomous.currentX = autonomous.explodeCenter.x;
      autonomous.currentY = autonomous.explodeCenter.y;
      autonomous.explodeCenter = autonomous.clickExplodeFrom || {
        x: rect.width / 2,
        y: rect.height / 2,
      };
    } else {
      autonomous.phase = 'glide';
    }

    autonomous.clickTriggered = false;
    autonomous.cycleStartTime = performance.now();
    autonomous.phaseStartTime = performance.now();
    autonomous.burstIntensity = 0;
    autonomous.explodeRadius = 0;

    // Set initial target
    autonomous.targetX = Math.random() * rect.width;
    autonomous.targetY = Math.random() * rect.height;

    const animate = () => {
      if (!autonomous.active) return;

      const now = performance.now();
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const phaseTime = now - autonomous.phaseStartTime;
      const totalCycleTime = autonomous.burstDuration + autonomous.glideDuration + autonomous.explodeDuration + autonomous.resetDuration;

      // Phase management
      if (autonomous.phase === 'glide' && phaseTime > autonomous.glideDuration) {
        autonomous.phase = 'burst'; // now burst happens after glide
        autonomous.phaseStartTime = now;
      } else if (autonomous.phase === 'burst' && phaseTime > autonomous.burstDuration) {
        autonomous.phase = 'explode';
        autonomous.phaseStartTime = now;
        autonomous.explodeRadius = 0;
        autonomous.explodeCenter = autonomous.clickExplodeFrom || {
          x: rect.width / 2,
          y: rect.height / 2,
        };

      } else if (autonomous.phase === 'explode' && phaseTime > autonomous.explodeDuration) {
        autonomous.phase = 'reset';
        autonomous.phaseStartTime = now;
      } else if (autonomous.phase === 'reset' && phaseTime > autonomous.resetDuration) {
        // End autonomous mode and return to user control
        stopAutonomousMode();
        // Restart after delay
        autonomous.timeoutId = setTimeout(() => {
          startAutonomousMode();
        }, autonomousDelay + 10000)
      }

      // Phase-specific behaviors
      if (autonomous.phase === 'burst') {
      // Inside `if (autonomous.phase === 'burst')` block
        const timeSinceLast = now - autonomous.lastRainbowTime;

        if (!autonomous.rainbowWash && timeSinceLast > autonomous.rainbowInterval) {
          autonomous.rainbowWash = true;
          autonomous.lastRainbowTime = now;

          setTimeout(() => {
            autonomous.rainbowWash = false;
          }, autonomous.rainbowDuration);
        }

        // Rapid bursts of activity
        const progress = phaseTime / autonomous.burstDuration;
        autonomous.burstIntensity = Math.sin(progress * Math.PI) * 2; // Peak in middle

        // Rapid target changes
        if (phaseTime % 300 < 16) { // Change target every 300ms
          autonomous.targetX = Math.random() * rect.width;
          autonomous.targetY = Math.random() * rect.height;
        }

        // Fast movement
        const dx = autonomous.targetX - autonomous.currentX;
        const dy = autonomous.targetY - autonomous.currentY;
        const distance = Math.hypot(dx, dy);
        if (distance > 1) {
          const glideSpeed = 2.5; // pixels per frame
          autonomous.currentX += (dx / distance) * glideSpeed;
          autonomous.currentY += (dy / distance) * glideSpeed;
        }
      } else if (autonomous.phase === 'glide') {
        // Smooth gliding movement
        if (phaseTime % 1500 < 16) { // Change target every 1.5s
          autonomous.targetX = Math.random() * rect.width;
          autonomous.targetY = Math.random() * rect.height;
        }

        // Smooth movement
        const dx = autonomous.targetX - autonomous.currentX;
        const dy = autonomous.targetY - autonomous.currentY;
        const distance = Math.hypot(dx, dy);
        if (distance > 1) {
          autonomous.currentX += (dx / distance) * autonomousSpeed * 0.8;
          autonomous.currentY += (dy / distance) * autonomousSpeed * 0.8;
        }

        // Gentle wave effects
        if (Math.random() < 0.03) {
          const waveX = autonomous.currentX + Math.sin(phaseTime * 0.002) * 80;
          const waveY = autonomous.currentY + Math.cos(phaseTime * 0.002) * 80;

          for (const dot of dotsRef.current) {
            const dist = Math.hypot(dot.cx - waveX, dot.cy - waveY);
            if (dist < proximity && !dot._inertiaApplied) {
              dot._inertiaApplied = true;
              gsap.killTweensOf(dot);
              const falloff = Math.max(0, 1 - dist / proximity);
              const pushX = (dot.cx - waveX) * shockStrength * falloff * 0.3;
              const pushY = (dot.cy - waveY) * shockStrength * falloff * 0.3;
              gsap.to(dot, {
                inertia: { xOffset: pushX, yOffset: pushY, resistance },
                onComplete: () => {
                  gsap.to(dot, {
                    xOffset: 0,
                    yOffset: 0,
                    duration: returnDuration,
                    ease: "elastic.out(1,0.75)",
                  });
                  dot._inertiaApplied = false;
                },
              });
            }
          }
        }

      } else if (autonomous.phase === 'explode') {
        // Explosive finale
        const progress = phaseTime / autonomous.explodeDuration;
        autonomous.explodeRadius = progress * Math.min(rect.width, rect.height) * 0.8;

        // Stay at center for explosion
        if (!autonomous.explodeCenter) {
          autonomous.explodeCenter = autonomous.clickExplodeFrom || {
            x: rect.width / 2,
            y: rect.height / 2,
          };
          autonomous.clickExplodeFrom = null;
        }

        autonomous.currentX = autonomous.explodeCenter.x;
        autonomous.currentY = autonomous.explodeCenter.y;
        // Explosive effect
        for (const dot of dotsRef.current) {
          const dist = Math.hypot(dot.cx - autonomous.currentX, dot.cy - autonomous.currentY);
          if (dist < autonomous.explodeRadius && !dot._inertiaApplied) {
            dot._inertiaApplied = true;
            gsap.killTweensOf(dot);
            const intensity = Math.max(0, 1 - dist / autonomous.explodeRadius);
            const pushX = (dot.cx - autonomous.currentX) * shockStrength * intensity * 2;
            const pushY = (dot.cy - autonomous.currentY) * shockStrength * intensity * 2;
            gsap.to(dot, {
              inertia: { xOffset: pushX, yOffset: pushY, resistance: resistance * 0.5 },
              onComplete: () => {
                gsap.to(dot, {
                  xOffset: 0,
                  yOffset: 0,
                  duration: returnDuration * 1.5,
                  ease: "elastic.out(1,0.6)",
                });
                dot._inertiaApplied = false;
              },
            });
          }
        }

      } else if (autonomous.phase === 'reset') {
        // Gentle reset phase - let dots settle
        autonomous.currentX = rect.width / 2;
        autonomous.currentY = rect.height / 2;
      }

      // Update pointer position for dot effects
      pointerRef.current.x = autonomous.currentX;
      pointerRef.current.y = autonomous.currentY;

      autonomous.animationId = requestAnimationFrame(animate);
    };

    animate();
  }, [autonomousSpeed, shockRadius, shockStrength, resistance, returnDuration, proximity, stopAutonomousMode]);


  const scheduleAutonomousMode = useCallback(() => {
    const autonomous = autonomousRef.current;

    // Clear existing timeout
    if (autonomous.timeoutId) {
      clearTimeout(autonomous.timeoutId);
    }

    // Stop current autonomous mode
    stopAutonomousMode();

    // Schedule new autonomous mode
    autonomous.timeoutId = setTimeout(() => {
      startAutonomousMode();
    }, autonomousDelay);
  }, [autonomousDelay, stopAutonomousMode, startAutonomousMode]);

  useEffect(() => {
    if (!circlePath) return;

    let rafId;
    const proxSq = proximity * proximity;

    const draw = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const { x: px, y: py } = pointerRef.current;

      for (let i = 0; i < dotsRef.current.length; i++) {
        const dot = dotsRef.current[i];
        const ox = dot.cx + dot.xOffset;
        const oy = dot.cy + dot.yOffset;
        const dx = dot.cx - px;
        const dy = dot.cy - py;
        const dsq = dx * dx + dy * dy;

        let style;
        const autonomous = autonomousRef.current;
        
        // Check if this dot is part of the user's name
        const isNameDot = actualNameDotsRef.current.some(nameDot => nameDot.index === i);

        if (isNameDot && nameSubmitted) {
          // Special styling for user's name dots - rainbow
          const time = performance.now() * 0.003;
          const hue = (time * 50 + i * 30) % 360;
          const saturation = 90 + Math.sin(time + i) * 10;
          const lightness = 60 + Math.sin(time * 1.5 + i) * 15;
          style = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        } else if (isNameDot && !nameSubmitted) {
          // Special styling for demo text dots - subtle glow
          const time = performance.now() * 0.002;
          const intensity = Math.sin(time + i * 0.1) * 0.3 + 0.7;
          const r = Math.round(activeRgb.r * intensity);
          const g = Math.round(activeRgb.g * intensity);
          const b = Math.round(activeRgb.b * intensity);
          style = `rgb(${r},${g},${b})`;
        } else if (effectMode === 'rainbow' || autonomous.rainbowWash) {
          const hue = ((dot.cx + dot.cy + performance.now() * 0.4) % 360);
          style = `hsl(${hue}, 100%, 60%)`;
        } else if (effectMode === 'pulse') {
          const time = performance.now() * 0.005;
          const pulseIntensity = Math.sin(time + i * 0.1) * 0.5 + 0.5;
          if (dsq <= proxSq) {
            const dist = Math.sqrt(dsq);
            const t = (1 - dist / proximity) * pulseIntensity;
            const r = Math.round(baseRgb.r + (activeRgb.r - baseRgb.r) * t);
            const g = Math.round(baseRgb.g + (activeRgb.g - baseRgb.g) * t);
            const b = Math.round(baseRgb.b + (activeRgb.b - baseRgb.b) * t);
            style = `rgb(${r},${g},${b})`;
          } else {
            const r = Math.round(baseRgb.r * (0.7 + pulseIntensity * 0.3));
            const g = Math.round(baseRgb.g * (0.7 + pulseIntensity * 0.3));
            const b = Math.round(baseRgb.b * (0.7 + pulseIntensity * 0.3));
            style = `rgb(${r},${g},${b})`;
          }
        } else if (effectMode === 'wave') {
          const time = performance.now() * 0.002;
          const waveX = Math.sin(time + dot.cx * 0.01) * 50;
          const waveY = Math.cos(time + dot.cy * 0.01) * 50;
          const waveDist = Math.hypot(dot.cx - (px + waveX), dot.cy - (py + waveY));
          const waveProx = proximity * 1.5;
          if (waveDist <= waveProx) {
            const t = 1 - waveDist / waveProx;
            const r = Math.round(baseRgb.r + (activeRgb.r - baseRgb.r) * t);
            const g = Math.round(baseRgb.g + (activeRgb.g - baseRgb.g) * t);
            const b = Math.round(baseRgb.b + (activeRgb.b - baseRgb.b) * t);
            style = `rgb(${r},${g},${b})`;
          } else {
            style = baseColor;
          }
        } else if (dsq <= proxSq) {
          // Normal mode - proper active color blending with heartbeat
          const dist = Math.sqrt(dsq);
          const t = 1 - dist / proximity;
          
          // Relaxing heartbeat pulse effect
          const time = performance.now() * 0.0015; // Slower timing
          const heartbeat = Math.sin(time * 1.2) * 0.25 + 0.75; // Slower, more gentle rhythm
          const pulseIntensity = t * heartbeat;
          
          const r = Math.round(baseRgb.r + (activeRgb.r - baseRgb.r) * pulseIntensity);
          const g = Math.round(baseRgb.g + (activeRgb.g - baseRgb.g) * pulseIntensity);
          const b = Math.round(baseRgb.b + (activeRgb.b - baseRgb.b) * pulseIntensity);
          style = `rgb(${r},${g},${b})`;
        } else {
          style = baseColor;
        }

        ctx.save();
        ctx.translate(ox, oy);
        ctx.fillStyle = style;
        
        // Scale up active dots with relaxing heartbeat pulse
        if (dsq <= proxSq && effectMode === 'normal') {
          const dist = Math.sqrt(dsq);
          const t = 1 - dist / proximity;
          const time = performance.now() * 0.0015; // Slower timing to match color
          const heartbeat = Math.sin(time * 1.2) * 0.15 + 0.85; // Gentler size pulse
          const scale = 1 + (t * 0.3 * heartbeat); // Up to 30% bigger with gentle pulse
          ctx.scale(scale, scale);
        }
        
        ctx.fill(circlePath);
        ctx.restore();
      }

      rafId = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(rafId);
  }, [proximity, baseColor, activeRgb, baseRgb, circlePath, effectMode]); // Removed nameSubmitted from deps

  useEffect(() => {
    buildGrid();
    let ro = null;
    if ("ResizeObserver" in window) {
      ro = new ResizeObserver(buildGrid);
      wrapperRef.current && ro.observe(wrapperRef.current);
    } else {
      window.addEventListener("resize", buildGrid);
    }

    // Start autonomous mode initially
    scheduleAutonomousMode();

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", buildGrid);
      stopAutonomousMode();
    };
  }, [buildGrid, scheduleAutonomousMode, stopAutonomousMode]);

  useEffect(() => {
    const onMove = (e) => {
      const now = performance.now();
      const pr = pointerRef.current;
      const dt = pr.lastTime ? now - pr.lastTime : 16;
      const dx = e.clientX - pr.lastX;
      const dy = e.clientY - pr.lastY;
      let vx = (dx / dt) * 1000;
      let vy = (dy / dt) * 1000;
      let speed = Math.hypot(vx, vy);
      if (speed > maxSpeed) {
        const scale = maxSpeed / speed;
        vx *= scale;
        vy *= scale;
        speed = maxSpeed;
      }
      pr.lastTime = now;
      pr.lastX = e.clientX;
      pr.lastY = e.clientY;
      pr.vx = vx;
      pr.vy = vy;
      pr.speed = speed;
      if (!canvasRef?.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      pr.x = e.clientX - rect.left;
      pr.y = e.clientY - rect.top;

      // Check if mouse is over the canvas
      if (pr.x >= 0 && pr.x <= rect.width && pr.y >= 0 && pr.y <= rect.height) {
        // Mouse is over canvas - stop autonomous mode and schedule new one
        scheduleAutonomousMode();
      }

      for (const dot of dotsRef.current) {
        const dist = Math.hypot(dot.cx - pr.x, dot.cy - pr.y);
        if (speed > speedTrigger && dist < proximity && !dot._inertiaApplied) {
          dot._inertiaApplied = true;
          gsap.killTweensOf(dot);
          const pushX = dot.cx - pr.x + vx * 0.005;
          const pushY = dot.cy - pr.y + vy * 0.005;
          gsap.to(dot, {
            inertia: { xOffset: pushX, yOffset: pushY, resistance },
            onComplete: () => {
              gsap.to(dot, {
                xOffset: 0,
                yOffset: 0,
                duration: returnDuration,
                ease: "elastic.out(1,0.75)",
              });
              dot._inertiaApplied = false;
            },
          });
        }
      }
    };

    const onClick = (e) => {
      const now = performance.now();
      const rect = canvasRef.current.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;

      // 💥 Handle shock animation (same as before)
      for (const dot of dotsRef.current) {
        const dist = Math.hypot(dot.cx - cx, dot.cy - cy);
        if (dist < shockRadius && !dot._inertiaApplied) {
          dot._inertiaApplied = true;
          gsap.killTweensOf(dot);
          const falloff = Math.max(0, 1 - dist / shockRadius);
          const pushX = (dot.cx - cx) * shockStrength * falloff;
          const pushY = (dot.cy - cy) * shockStrength * falloff;
          gsap.to(dot, {
            inertia: { xOffset: pushX, yOffset: pushY, resistance },
            onComplete: () => {
              gsap.to(dot, {
                xOffset: 0,
                yOffset: 0,
                duration: returnDuration,
                ease: "elastic.out(1,0.75)",
              });
              dot._inertiaApplied = false;
            },
          });
        }
      }

      // 🧠 Track clicks
      const clickTimestamps = clickTrackerRef.current;
      const threshold = 2000; // 2 seconds
      const requiredClicks = 4;

      // Remove old clicks
      clickTimestamps.push(now);
      clickTrackerRef.current = clickTimestamps.filter(
        (timestamp) => now - timestamp < threshold
      );

      if (clickTrackerRef.current.length >= requiredClicks) {
        clickTrackerRef.current = [];
        const rect = canvasRef.current.getBoundingClientRect();
        autonomousRef.current.clickTriggered = true;
        autonomousRef.current.clickExplodeFrom = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
        startAutonomousMode();
      } else {
        // Otherwise just restart timer
        scheduleAutonomousMode();
      }
    };


    const throttledMove = throttle(onMove, 24);
    window.addEventListener("mousemove", throttledMove, { passive: true });
    window.addEventListener("click", onClick);

    return () => {
      window.removeEventListener("mousemove", throttledMove);
      window.removeEventListener("click", onClick);
    };
  }, [
    maxSpeed,
    speedTrigger,
    proximity,
    resistance,
    returnDuration,
    shockRadius,
    shockStrength,
    scheduleAutonomousMode,
  ]);

  return (
    <section
      className={`p-4 flex items-center justify-center h-full w-full relative ${className}`}
      style={style}
    >
      <div ref={wrapperRef} className="w-full h-full relative">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
        />
      </div>
    </section>
  );
};

export default memo(DotGrid);